import {
    Alignment,
    detectOverflow,
    DetectOverflowOptions,
    Middleware,
    Placement,
    Side,
    SizeOptions,
} from '@floating-ui/react-dom';

/**
 * Возвращает строку с добавленным суффиксом px
 * @kind helper
 * @param value числовое значение
 *
 * @returns строка с добавленным суффиксом px
 */
export function getSizeStyle(value: number) {
    return `${value}px`;
}

/**
 * Возвращает минимальное из переданных размеров
 * @kind helper
 * @param specified заданное ограничение
 * @param calculated вычисленное значение
 *
 * @returns минимальное из переданных значений или значение одного из непустых параметров
 */
export function limitValue(specified?: number, calculated?: number): string | undefined {
    const valueFromBoth = specified && calculated ? Math.min(specified, calculated) : undefined;

    if (valueFromBoth) {
        return getSizeStyle(valueFromBoth);
    }

    if (calculated) {
        return getSizeStyle(calculated);
    }

    if (specified) {
        return getSizeStyle(specified);
    }

    return undefined;
}

export type CustomSizeOptions = Partial<SizeOptions & DetectOverflowOptions>;

function getSide(placement: Placement): Side {
    // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return placement.split('-')[0] as Side;
}

function getAlignment(placement: Placement): Alignment | undefined {
    // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return placement.split('-')[1] as Alignment;
}

export const size = (options: CustomSizeOptions): Middleware => ({
    name: 'size',
    options,
    async fn(middlewareArguments) {
        const { placement, rects, platform, elements } = middlewareArguments;
        const { apply = () => {}, ...detectOverflowOptions } = options;

        const overflow = await detectOverflow(middlewareArguments, detectOverflowOptions);
        const side = getSide(placement);
        const alignment = getAlignment(placement);

        let heightSide: 'top' | 'bottom';
        let widthSide: 'left' | 'right';

        if (side === 'top' || side === 'bottom') {
            heightSide = side;
            widthSide =
                alignment === ((await platform.isRTL?.(elements.floating)) ? 'start' : 'end') ? 'left' : 'right';
        } else {
            widthSide = side;
            heightSide = alignment === 'end' ? 'top' : 'bottom';
        }

        const dimensions = {
            availableHeight: rects.floating.height - overflow[heightSide],
            availableWidth: rects.floating.width - overflow[widthSide],
        };

        await apply({ ...middlewareArguments, ...dimensions });

        const nextDimensions = await platform.getDimensions(elements.floating);

        if (rects.floating.width !== nextDimensions.width || rects.floating.height !== nextDimensions.height) {
            return {
                reset: {
                    rects: true,
                },
            };
        }

        return {};
    },
});
